/**
 * Calendar time helpers. The server stores UTC instants (CAL-023); the calendar
 * shows UTC (CAL-036) and names the organiser's zone where it differs. Forms
 * take wall-clock times in a named zone and convert them to UTC (CAL-035).
 */

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/** `YYYY-MM` of an ISO instant or date. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

export function addMonths(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return d.toISOString().slice(0, 7)
}

/** First and last day (`YYYY-MM-DD`) of a month, for the range query. */
export function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

/** Day cells for a Monday-first month grid: leading blanks, then dates. */
export function monthCells(month: string): (string | null)[] {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const first = new Date(Date.UTC(y, m - 1, 1))
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const offset = (first.getUTCDay() + 6) % 7
  const cells: (string | null)[] = Array.from({ length: offset }, () => null)
  for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`)
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

export function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function timeUtc(iso: string): string {
  return iso.slice(11, 16)
}

export function dayNumber(iso: string): string {
  return String(new Date(iso).getUTCDate())
}

export function shortMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' })
}

export function dateUtc(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function dateTimeUtc(iso: string): string {
  return `${dateUtc(iso)}, ${timeUtc(iso)} UTC`
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** "Wed, 4 Nov 2026, 13:00–14:00 UTC" (end date spelled out when it differs). */
export function rangeUtc(starts: string, ends: string): string {
  if (starts.slice(0, 10) === ends.slice(0, 10)) {
    return `${dateUtc(starts)}, ${timeUtc(starts)}–${timeUtc(ends)} UTC`
  }
  return `${dateTimeUtc(starts)} – ${dateTimeUtc(ends)}`
}

function zoneFormat(zone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: zone })
  } catch {
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' })
  }
}

/** "CET", "CEST", "UTC": the zone's short name at that instant, or the IANA name. */
export function zoneAbbr(iso: string, zone: string): string {
  if (zone === 'UTC') return 'UTC'
  try {
    const part = new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(new Date(iso))
      .find((p) => p.type === 'timeZoneName')
    return part?.value ?? zone.replace(/_/g, ' ')
  } catch {
    return 'UTC'
  }
}

/** "14:00" on the organiser's clock. */
export function timeIn(iso: string, zone: string): string {
  return zoneFormat(zone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(iso),
  )
}

/** "Tue, 24 Mar 2026" on the organiser's calendar. */
export function dateIn(iso: string, zone: string): string {
  return zoneFormat(zone, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso))
}

/** "24 Mar 2026, 14:00 CET": the meeting's local time, for lists and cards. */
export function dateTimeIn(iso: string, zone: string): string {
  const day = zoneFormat(zone, { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(iso),
  )
  return `${day}, ${timeIn(iso, zone)} ${zoneAbbr(iso, zone)}`
}

/** "Tue, 24 Mar 2026, 14:00–15:00 CET" (end date spelled out when it differs). */
export function rangeIn(starts: string, ends: string, zone: string): string {
  const sameDay = dateIn(starts, zone) === dateIn(ends, zone)
  if (sameDay) {
    return `${dateIn(starts, zone)}, ${timeIn(starts, zone)}–${timeIn(ends, zone)} ${zoneAbbr(starts, zone)}`
  }
  return `${dateIn(starts, zone)}, ${timeIn(starts, zone)} – ${dateIn(ends, zone)}, ${timeIn(ends, zone)} ${zoneAbbr(starts, zone)}`
}

/** "14:00–15:00 Europe/Oslo" for the organiser's zone, or null when it is UTC. */
export function rangeInZone(starts: string, ends: string, zone: string): string | null {
  if (zone === 'UTC') return null
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: zone,
    })
    return `${fmt.format(new Date(starts))}–${fmt.format(new Date(ends))} ${zone.replace(/_/g, ' ')}`
  } catch {
    return null
  }
}

function zoneOffsetMs(instant: number, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant))
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  )
  return asUtc - instant
}

/** `YYYY-MM-DDTHH:mm` wall-clock time in `zone` → UTC ISO instant. */
export function zonedToUtc(local: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local)
  if (!match) return null
  const [, y, mo, d, h, mi] = match.map(Number) as [number, number, number, number, number, number]
  const guess = Date.UTC(y, mo - 1, d, h, mi)
  if (zone === 'UTC') return new Date(guess).toISOString()
  try {
    let utc = guess - zoneOffsetMs(guess, zone)
    utc = guess - zoneOffsetMs(utc, zone)
    return new Date(utc).toISOString()
  } catch {
    return null
  }
}

/** UTC ISO instant → `YYYY-MM-DDTHH:mm` wall-clock time in `zone` (for form inputs). */
export function utcToZoned(iso: string, zone: string): string {
  const instant = Date.parse(iso)
  const offset = zone === 'UTC' ? 0 : zoneOffsetMs(instant, zone)
  return new Date(instant + offset).toISOString().slice(0, 16)
}

export const COMMON_ZONES = [
  'UTC',
  'Europe/Oslo',
  'Europe/Stockholm',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'Asia/Singapore',
  'Asia/Tokyo',
] as const

export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function zoneOptions(current?: string): { value: string; label: string }[] {
  const zones = new Set<string>(COMMON_ZONES)
  zones.add(browserZone())
  if (current) zones.add(current)
  return [...zones].map((z) => ({ value: z, label: z.replace(/_/g, ' ') }))
}
